import { useState, useLayoutEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import { toast } from "react-toastify";
import CheckIcon from "@mui/icons-material/Check";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import { Text } from "@components";
import theme from "../../theme";
import golfCartBlack from "@assets/images/golfCartBlack.png";
import Tooltip from "./toolTip";
import { updateCart, removeTeetime, editCart } from "@redux/booking/slice";

const BookTeeTimeCard = ({
  teetime,
  destination,
  onToggleToolTip = () => {},
}) => {
  const dispatch = useDispatch();
  const { cartDetails } = useSelector((store) => store.bookingReducer);
  const { t } = useTranslation("common");
  const [anchorEl, setAnchorEl] = useState(null);
  const [playerCount, setPlayerCount] = useState(0);
  const onPlayerCountChange = (e) => setPlayerCount(e);

  const playerCountHelper = () => {
    let item = cartDetails?.filter((e) => e?.clubId == destination?.id);
    item = item?.filter((e) => e?.teeId == teetime?.teeid);
    item = item?.filter((e) => e?.course == teetime?.course);
    item = item?.filter((e) => e?.completeDetails?.time == teetime?.time);
    let playersCount = item?.[0]?.players - 1 || 0;
    setPlayerCount(playersCount);
  };

  const onCloseModal = () => {
    setAnchorEl(null);
    const isClubTeeInCart = checkClubTeeInCart();
    if (!isClubTeeInCart) {
      let firstClubPlayersCount = getFirstClubPlayersCount();
      let maxPlayerTee = teetime?.products?.[0]?.available;
      firstClubPlayersCount = Math.min(maxPlayerTee, firstClubPlayersCount);
      setPlayerCount(Math.max(0, firstClubPlayersCount - 1));
    } else {
      playerCountHelper();
    }
  };

  const handleCardClick = (event) => {
    event.stopPropagation();
    if (anchorEl) {
      setAnchorEl(null);
      onToggleToolTip(false);
    } else {
      const isClubTeeInCart = checkClubTeeInCart();
      if (!isClubTeeInCart) {
        let firstClubPlayersCount = getFirstClubPlayersCount();
        let maxPlayerTee = teetime?.products?.[0]?.available;
        firstClubPlayersCount = Math.min(maxPlayerTee, firstClubPlayersCount);
        setPlayerCount(Math.max(0, firstClubPlayersCount - 1));
      }
      setAnchorEl(event.currentTarget);
      onToggleToolTip(true);
    }
  };

  const getTime = (time) => {
    const date = new Date(time);
    const formattedTime = date.toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
    return formattedTime;
  };

  const goToCheckout = (
    tee = teetime,
    key = "cart",
    clubName = destination?.name,
    clubId = destination?.id
  ) => {
    const course_ID = destination?.courses?.find(
      (e) => e?.courseName === teetime?.course
    );
    const singleBookingData = {
      teeId: tee?.teeid,
      course: tee?.course,
      courseId: course_ID,
      players: playerCount + 1,
      availablePlayer: tee?.products[0]?.available,
      teetime: dayjs(tee?.time).format("YYYY-MM-DD HH:mm:ss"),
      holes: tee?.holes,
      clubName: clubName,
      allPrice: tee?.products[0]?.pricing,
      clubId: clubId,
      facilityName: clubName,
      extraProduct: tee?.extraProducts,
      completeDetails: tee,
    };
    dispatch(updateCart(singleBookingData));
    toast.warning(t("course.item_Add"), {
      icon: <CheckCircleIcon className="text-buttonColor" />,
    });
  };

  const removeFromCart = (shouldRemove = false) => {
    if (shouldRemove) {
      let itemsToRemove = cartDetails?.filter(
        (e) =>
          e?.clubId != destination?.id ||
          e?.teeId != teetime?.teeid ||
          e?.course != teetime?.course ||
          e?.completeDetails?.time != teetime?.time
      );
      dispatch(removeTeetime(itemsToRemove));
      toast.error(t("course.item_Remove"), {});
    } else {
      let localdata = JSON.parse(localStorage.getItem("C2H_LocalCartData"));
      let upd_obj = localdata?.map((obj) => {
        if (
          obj?.clubId === destination?.id &&
          obj?.teeId === teetime?.teeid &&
          obj?.course === teetime?.course &&
          obj?.completeDetails?.time === teetime?.time
        ) {
          obj.players = Number(playerCount + 1);
        }
        return obj;
      });
      dispatch(editCart(upd_obj));
    }
  };

  const checkSelected = (item) => {
    const selected = cartDetails?.find(
      (val, ind) =>
        val?.teeId === item.teeid &&
        dayjs(val?.teetime).format("YYYY-MM-DD HH:mm:ss") ===
          dayjs(item.time).format("YYYY-MM-DD HH:mm:ss") &&
        val?.course === item.course &&
        val?.clubName === destination?.name
    );
    return selected;
  };

  const getFirstClubPlayersCount = () => {
    let firstClubId = cartDetails?.[0]?.clubId;
    let firstClubTeeList = cartDetails.filter((e) => e.clubId == firstClubId);
    let firstClubPlayersCount = 0;
    firstClubTeeList.forEach((e) => (firstClubPlayersCount += e?.players));
    return firstClubPlayersCount;
  };

  const checkClubTeeInCart = () => {
    let clubTeeList = cartDetails.filter((e) => e.clubId == destination.id);
    return Boolean(clubTeeList.length);
  };

  useLayoutEffect(() => {
    const isClubTeeInCart = checkClubTeeInCart();
    if (!isClubTeeInCart) {
      let firstClubPlayersCount = getFirstClubPlayersCount();
      firstClubPlayersCount = Math.min(4, firstClubPlayersCount);
      setPlayerCount(Math.max(0, firstClubPlayersCount - 1));
    } else {
      playerCountHelper();
    }
  }, [cartDetails]);

  return (
    <div
      onClick={handleCardClick}
      className="bg-buttonColor rounded-lg relative hover:cursor-pointer hover:opacity-90 hover:shadow-lg transition-all duration-200 ease-in-out"
    >
      <Tooltip
        players={teetime?.products?.[0]?.available}
        anchorEl={anchorEl}
        onCloseModal={onCloseModal}
        onAddToCart={goToCheckout}
        onRemoveFromCart={(e) => removeFromCart(e)}
        playerCount={playerCount}
        onPlayerCountChange={onPlayerCountChange}
        teeInCart={checkSelected(teetime)}
        teeid={teetime?.teeid}
        cartDetails={cartDetails}
        clubId={destination?.id}
        course={teetime?.course}
        time={teetime?.time}
      />
      <div className="py-2 flex justify-center items-center relative">
        <div className="flex items-center gap-1">
          <div className="flex justify-center items-center bg-white h-5 w-5 rounded-full">
            <p className="text-buttonColor text-[16px]">
              {teetime?.products?.[0]?.available -
                (checkSelected(teetime)?.players || 0)}
            </p>
          </div>
          <Text className="text-buttonTextColor">{t("common.available")}</Text>
          {checkSelected(teetime) && (
            <div
              style={{ borderBottomLeftRadius: "100%" }}
              className="right-0 top-0 absolute bg-buttonTextColor pb-2 pl-2 border border-buttonColor rounded-lg"
            >
              <CheckIcon sx={{ color: theme.colors.buttonColor }} />
            </div>
          )}
        </div>
      </div>
      <div
        className={`bg-white px-0 py-2 flex flex-col border-buttonColor border-l border-r`}
      >
        <div className="flex justify-center items-center gap-1">
          <AccessTimeIcon sx={{ fontSize: 20 }} />
          <Text className=" font-bold text-[16px]">
            {getTime(teetime?.time)}
          </Text>
        </div>
      </div>
      <div className="py-1 flex justify-center items-center gap-2">
        <div className="h-6 w-6 bg-white rounded-full flex justify-center items-center">
          <img src={golfCartBlack} className="w-[16px] h-[13px]" alt={""} />
        </div>
        <Text className="text-buttonTextColor text-center font-normal text-[16px]">
          {t("common.bookNowCaps")}
        </Text>
      </div>
    </div>
  );
};

export default BookTeeTimeCard;
